import type { ActivityEvent, Execution, FindingConfidence, Message, OpenPointKind, RunReport, WorkHistoryEntry, WorkItem, WorkItemDetail } from '@deskworks/shared-types';
import { ACTIVE_EXECUTION_STATUSES } from '@deskworks/shared-types';
import { ArrowRight, CheckCircle2, ChevronRight, CircleAlert, ClipboardList, Clock, Link2, Loader2, ShieldCheck, User, XCircle } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { activityIcon } from '@/components/activity-feed';
import { AgentAvatar } from '@/components/agent-avatar';
import { AgentSteps } from '@/components/agent-steps';
import { Markdown } from '@/components/markdown';
import { SessionLink } from '@/components/session-link';
import { formatAbsolute, formatDay, formatDuration, formatSince, formatTime } from '@/lib/format';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { qk } from '@/lib/query-keys';
import { isPersonTask, PersonTaskCard } from '@/components/person-task-card';
import { PendingApprovalCard } from './approval-card';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * The course of a Work Item as one conversation, oldest first like a chat (docs/features/work-item-chat.md#the-work-log):
 * who created it and why, each run of the Lead and its agents with what it did and found, whom the Lead handed the
 * work to, what people wrote and decided, the sub-items handed out and what came back — and at the end what it waits
 * for now, with the card to decide on.
 */

/** Steps that say nothing about the work: a worker starting or resuming, a run's start (its brief is the hand-off). */
const QUIET = new Set(['agent.lifecycle', 'agent.started', 'run.started', 'status.working', 'status.ready', 'status.waiting', 'status.needs_approval']);

const time = (s: string | null | undefined) => (s ? new Date(s).getTime() : Number.NaN);
const isActive = (r: Execution) => ACTIVE_EXECUTION_STATUSES.includes(r.status);

/** The item's own runs (not side chats or follow-ups), oldest first. */
function itemRuns(item: WorkItemDetail, runs: Execution[]): Execution[] {
  return runs
    .filter((r) => r.workItemId === item.id && !r.sideChat && (r.kind === 'work_item' || r.kind === 'lead'))
    .sort((a, b) => time(a.createdAt) - time(b.createdAt));
}

/** The Lead's runs of its own, oldest first: not the ones that only took a finished agent run's result in. */
export function ownLeadRuns(item: WorkItemDetail, runs: Execution[]): string[] {
  const out: string[] = [];
  let last: Execution | undefined;
  for (const r of itemRuns(item, runs)) {
    if (r.kind === 'lead' && last && last.kind !== 'lead' && last.finishedAt && time(r.createdAt) >= time(last.finishedAt)) continue;
    if (r.kind === 'lead') out.push(r.id);
    last = r;
  }
  return out;
}

/** Deskworks' summary when the last sub-item ended; the sub-items stand in the course themselves. */
export function childrenDone(m: Message): boolean {
  return (
    m.senderType === 'system' &&
    (m.metadata?.event === 'children_done' || m.metadata?.event === 'child_done' || m.content.startsWith('All child Work Items are done'))
  );
}

/** How a Lead run was woken, from its brief ("Woken by: run.finished, …"), in words. */
export function wokenBy(event: string): string {
  switch (event) {
    case 'run.finished':
      return t('a run ended');
    case 'run.failed':
      return t('a run failed');
    case 'message.received':
      return t('a message');
    case 'work_item.continued':
      return t('it was continued');
    case 'work_item.reopened':
      return t('it was reopened');
    case 'work_item.created':
      return t('it was created');
    case 'work_item.approved':
      return t('it was approved to start');
    case 'approval.resolved':
      return t('a decision');
    case 'question.answered':
      return t('an answer');
    case 'decision.needed':
      return t('a decision to make');
    case 'wake.due':
      return t('the time it set to look again');
    default:
      return event;
  }
}

function wokenEvents(brief: string | null | undefined): string[] | null {
  const b = (brief ?? '').trim();
  if (!/^Woken by:/i.test(b)) return null;
  return b
    .replace(/^Woken by:\s*/i, '')
    .split(/[,;]\s*/)
    .map((w) => w.trim())
    .filter(Boolean);
}

export function wokenReasons(brief: string | null | undefined): string {
  return (wokenEvents(brief) ?? []).map(wokenBy).join(', ');
}

/** What a run did, in a few words after the agent's name: "took the work in", "looked again, as planned" … */
function runAction(run: Execution, lead: boolean, failed: boolean): string {
  if (!lead) return isActive(run) ? t('is working on its assignment') : failed ? t('could not finish its assignment') : t('worked on its assignment');
  const events = wokenEvents(run.brief);
  if (!events) return isActive(run) ? t('is taking the work in') : t('took the work in');
  switch (events[0]) {
    case 'work_item.created':
    case 'work_item.approved':
      return isActive(run) ? t('is taking the work in') : t('took the work in');
    case 'run.finished':
      return t('took the result in');
    case 'run.failed':
      return t('looked at the failed run');
    case 'wake.due':
      return t('looked again, as it had planned');
    case 'message.received':
      return t('read a new message');
    case 'approval.resolved':
      return t('went on after the decision');
    case 'question.answered':
      return t('went on after the answer');
    case 'work_item.continued':
      return t('went on after it was continued');
    case 'work_item.reopened':
      return t('took it up again after it was reopened');
    default:
      return t('was woken: {reason}', { reason: events.map(wokenBy).join(', ') });
  }
}

export type LogEntry =
  | { kind: 'created'; at: number; key: string }
  /**
   * A run as its one message (docs/features/work-item-chat.md#the-result-of-a-run): what it did, its result, what it
   * said along the way (a question) and, for the Lead, whom it handed the work to.
   */
  | {
      kind: 'run';
      at: number;
      key: string;
      run: Execution;
      done: WorkHistoryEntry[];
      finding: string;
      briefed: boolean;
      said: Message[];
      handoffs: { message: Message; target: Execution | undefined }[];
    }
  /** The Lead hands the work to an agent (start_run): the brief, and the run it started. */
  | { kind: 'handoff'; at: number; key: string; message: Message; target: Execution | undefined }
  | { kind: 'message'; at: number; key: string; message: Message }
  /** A person (or the Lead in God mode) decided an approval. */
  | { kind: 'decision'; at: number; key: string; message: Message }
  /** What someone wrote down without a run of the item, e.g. a person who did a step themselves. */
  | { kind: 'note'; at: number; key: string; entry: WorkHistoryEntry }
  | { kind: 'child'; at: number; key: string; child: WorkItem; back: boolean };

const ORDER: Record<LogEntry['kind'], number> = { created: 0, handoff: 1, run: 2, message: 3, decision: 3, note: 3, child: 3 };

/**
 * Everything of the item in the order it happened; every message stands in exactly one place: a run's result is its
 * finding, the Lead's brief is the hand-off, a decision is one line.
 */
export function logEntries(item: WorkItemDetail, messages: Message[], runs: Execution[], history: WorkHistoryEntry[]): LogEntry[] {
  const own = itemRuns(item, runs);
  const ids = new Set(own.map((r) => r.id));
  const finished = new Map<string, Message>();
  const briefed = new Set<string>();
  const out: LogEntry[] = [{ kind: 'created', at: time(item.createdAt), key: 'created' }];
  const said = new Map<string, Message[]>();
  const handoffs = new Map<string, { message: Message; target: Execution | undefined }[]>();
  const push = <T,>(map: Map<string, T[]>, key: string, value: T) => map.set(key, [...(map.get(key) ?? []), value]);
  for (const m of messages) {
    if (childrenDone(m)) continue;
    const event = m.metadata?.event;
    // What a run of the item wrote stands in its one message.
    const ofRun = m.senderType === 'agent' && m.executionId && ids.has(m.executionId) ? m.executionId : null;
    if (event === 'run_finished' && ofRun) {
      finished.set(ofRun, m);
      continue;
    }
    if (event === 'run_started') {
      // The run a hand-off started: the next run of that agent after it.
      const target = own.find((r) => r.kind === 'work_item' && r.agentId === m.metadata?.agentId && !briefed.has(r.id) && time(r.createdAt) >= time(m.createdAt) - 5_000);
      if (target) briefed.add(target.id);
      if (ofRun) push(handoffs, ofRun, { message: m, target });
      else out.push({ kind: 'handoff', at: time(m.createdAt), key: m.id, message: m, target });
      continue;
    }
    if (ofRun) {
      push(said, ofRun, m);
      continue;
    }
    if (m.senderType === 'system' && typeof m.metadata?.approvalId === 'string') {
      out.push({ kind: 'decision', at: time(m.createdAt), key: m.id, message: m });
      continue;
    }
    out.push({ kind: 'message', at: time(m.createdAt), key: m.id, message: m });
  }
  const done = new Map<string, WorkHistoryEntry[]>();
  for (const h of history) {
    if (h.executionId && ids.has(h.executionId)) done.set(h.executionId, [...(done.get(h.executionId) ?? []), h]);
    else out.push({ kind: 'note', at: time(h.createdAt), key: h.id, entry: h });
  }
  for (const r of own) {
    const entries = done.get(r.id) ?? [];
    // The item's result stands at the end of the course; the run that produced it does not repeat it.
    const result = r.id === item.resultExecutionId && item.result?.trim() ? '' : r.result?.trim() || finished.get(r.id)?.content.trim() || '';
    const finding = entries.some((h) => h.summary.trim() === result) ? '' : result;
    out.push({
      kind: 'run',
      at: time(r.startedAt ?? r.createdAt),
      key: r.id,
      run: r,
      done: entries,
      finding,
      briefed: briefed.has(r.id),
      said: said.get(r.id) ?? [],
      handoffs: handoffs.get(r.id) ?? [],
    });
  }
  for (const c of item.children.filter((c) => c.kind !== 'review')) {
    out.push({ kind: 'child', at: time(c.createdAt), key: `${c.id}:out`, child: c, back: false });
    if (c.completedAt) out.push({ kind: 'child', at: time(c.completedAt), key: `${c.id}:back`, child: c, back: true });
  }
  return out.sort((a, b) => a.at - b.at || ORDER[a.kind] - ORDER[b.kind]);
}

/** What the work waits for now, in one line; null while a run shows it is working or there is nothing to say. */
function waitsFor(item: WorkItemDetail, running: boolean): { text: string; you: boolean } | null {
  const lead = item.lead?.name ?? t('the Lead');
  if (running) return null;
  if (item.paused) return { text: t('Paused — it goes on once it is continued.'), you: false };
  switch (item.status) {
    case 'cancelled':
      return { text: t('Cancelled.'), you: false };
    case 'failed':
      return { text: t('The work failed.'), you: false };
    case 'blocked':
      return { text: item.waitingReason ? t('Blocked: {reason}', { reason: item.waitingReason }) : t('Blocked'), you: true };
    case 'needs_approval':
      return { text: t('Waiting for your decision.'), you: true };
    case 'waiting':
      if (item.waitingReason === 'wake' && item.wakeAt) return { text: t('Looks again {time}', { time: formatAbsolute(item.wakeAt) }), you: false };
      if (item.waitingReason === 'children') return { text: t('Waiting for its sub-items.'), you: false };
      if (item.withLead && !item.questionOpen) return { text: t('{lead} decides the next step', { lead }), you: false };
      return { text: t('Waiting for your answer — write it below.'), you: true };
    case 'ready':
    case 'working':
      return { text: t('The next run starts shortly.'), you: false };
    default:
      return null;
  }
}

export function scrollToRun(runId: string): boolean {
  const el = document.getElementById(`run-${runId}`);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.classList.add('ring-2', 'ring-indigo-300');
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-300'), 1600);
  return true;
}

export function WorkLog({
  item,
  messages,
  activity,
  runs,
  history,
  createdBy,
  live,
  end,
  renderMessage,
  renderChild,
}: {
  item: WorkItemDetail;
  messages: Message[];
  activity: ActivityEvent[];
  runs: Execution[];
  history: WorkHistoryEntry[];
  /** Who created the item, when the activity says (an agent). */
  createdBy: string | null;
  /** An answer in the chat being written right now. */
  live?: ReactNode;
  /** What closes the course: the result, the card to decide on. */
  end?: ReactNode;
  renderMessage: (m: Message) => ReactNode;
  renderChild: (child: WorkItem, back: boolean) => ReactNode;
}) {
  const entries = logEntries(item, messages, runs, history);
  const running = !!item.currentExecution && isActive(item.currentExecution);
  const next = waitsFor(item, running);
  const day = (at: number) => new Date(at).toDateString();
  return (
    <div className="flex flex-col gap-5">
      {entries.map((e, i) => (
        <Fragment key={e.key}>
          {(i === 0 || day(e.at) !== day(entries[i - 1].at)) && <DayDivider at={e.at} />}
          {e.kind === 'created' ? (
            <CreatedEntry item={item} createdBy={createdBy} />
          ) : e.kind === 'run' ? (
            <RunEntry entry={e} item={item} activity={activity} />
          ) : e.kind === 'handoff' ? (
            <HandoffEntry message={e.message} target={e.target} workspaceId={item.workspaceId} />
          ) : e.kind === 'decision' ? (
            <DecisionEntry message={e.message} />
          ) : e.kind === 'note' ? (
            <NoteEntry entry={e.entry} workspaceId={item.workspaceId} />
          ) : e.kind === 'child' ? (
            <div>{renderChild(e.child, e.back)}</div>
          ) : (
            <MessageEntry message={e.message} render={renderMessage} />
          )}
        </Fragment>
      ))}
      {live}
      {next && (
        <p className={cn('flex items-start justify-center gap-1.5 text-center text-[13px] font-medium', next.you ? 'text-amber-800 dark:text-amber-300' : 'text-muted-foreground')}>
          <Clock className="mt-0.5 size-3.5 shrink-0" /> {next.text}
        </p>
      )}
      {end}
      {childrenWaitingForYou(item).map((c) => (
        <ChildDecision key={c.id} child={c} parent={item} />
      ))}
    </div>
  );
}

/** Sub-items that wait for a person's decision or answer, longest waiting first. */
export function childrenWaitingForYou(item: WorkItemDetail) {
  return item.children
    .filter((c) => !c.paused && (c.status === 'needs_approval' || (c.status === 'waiting' && c.questionOpen)))
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));
}

/**
 * What a sub-item waits for from a person, decided right here (docs/features/work-items.md#waiting-on-a-sub-item): its
 * approval card or its question, with the desk and the Work Item it belongs to.
 */
function ChildDecision({ child, parent }: { child: WorkItem; parent: WorkItemDetail }) {
  const detail = useQuery({ queryKey: qk.workItemDetail(child.id), queryFn: () => api.workItems.get(child.id) });
  const pending = detail.data?.approvals.find((a) => a.status === 'pending');
  const otherDesk = child.deskId !== parent.deskId;
  return (
    <div className="flex flex-col gap-2">
      <p className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-muted-foreground">
        <ShieldCheck className="size-3.5 shrink-0 text-amber-600" />
        {otherDesk ? t('At the desk {desk}:', { desk: child.deskName ?? t('another desk') }) : t('Sub-item:')}
        <Link
          to="/w/$workspaceId/work-items/$workItemId"
          params={{ workspaceId: child.workspaceId, workItemId: child.id }}
          className="font-medium text-foreground hover:text-primary hover:underline"
        >
          {child.title}
        </Link>
        <span>· {t('waits')} {formatSince(child.updatedAt) ?? t('just now')}</span>
      </p>
      {detail.data && pending ? (
        isPersonTask(pending) ? (
          <PersonTaskCard approval={pending} workspaceId={child.workspaceId} />
        ) : (
          <PendingApprovalCard item={detail.data} approval={pending} />
        )
      ) : (
        detail.data && (
          <Link
            to="/w/$workspaceId/work-items/$workItemId"
            params={{ workspaceId: child.workspaceId, workItemId: child.id }}
            className="w-fit rounded-md border border-amber-300 bg-amber-50/60 px-3 py-1.5 text-[13px] font-medium hover:underline dark:border-amber-800 dark:bg-amber-950/30"
          >
            {t('Answer it there')}
          </Link>
        )
      )}
    </div>
  );
}

function DayDivider({ at }: { at: number }) {
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span aria-hidden className="h-px flex-1 bg-border" />
      {formatDay(new Date(at).toISOString())}
      <span aria-hidden className="h-px flex-1 bg-border" />
    </div>
  );
}

/** One line above a bubble: who, what they did, when. */
function Byline({ children }: { children: ReactNode }) {
  return <div className="mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">{children}</div>;
}

function Name({ children }: { children: ReactNode }) {
  return <span className="font-medium text-foreground/85">{children}</span>;
}

function Role({ lead }: { lead: boolean }) {
  return lead ? (
    <span className="rounded-full bg-amber-500/10 px-1.5 py-px text-[11px] text-amber-800 dark:text-amber-300">{t('Lead')}</span>
  ) : (
    <span className="rounded-full bg-indigo-500/10 px-1.5 py-px text-[11px] text-indigo-700 dark:text-indigo-300">{t('Agent')}</span>
  );
}

function When({ at }: { at: string }) {
  return (
    <time dateTime={at} title={formatAbsolute(at, true)} className="tabular-nums">
      {formatTime(at)}
    </time>
  );
}

/** How it started: who created the item, and the task as given. */
function CreatedEntry({ item, createdBy }: { item: WorkItemDetail; createdBy: string | null }) {
  const description = item.description?.trim();
  const goal = item.goal?.trim();
  const who =
    item.createdByType === 'agent'
      ? (createdBy ?? item.lead?.name ?? t('An agent'))
      : item.createdByType === 'user'
        ? t('A person')
        : t('A trigger');
  const ref = item.externalReference;
  return (
    <div className="flex gap-2.5">
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground max-sm:hidden">
        {item.createdByType === 'user' ? <User className="size-3.5" /> : <ClipboardList className="size-3.5" />}
      </div>
      <div className="min-w-0 flex-1">
        <Byline>
          <Name>{who}</Name>
          <span>{t('created the work')}</span>
          <When at={item.createdAt} />
          {ref && (
            <span className="rounded-full bg-muted px-1.5 py-px font-mono text-[11px]" title={`${ref.type}:${ref.id}`}>
              {ref.type} {ref.id.slice(0, 8)}
            </span>
          )}
        </Byline>
        <div className="rounded-2xl rounded-tl-sm border bg-muted/40 px-3.5 py-2.5">
          <p className="text-sm font-medium">{item.title}</p>
          {description && (
            <Clamp text={description} limit={700} height="max-h-48" className="mt-1" />
          )}
          {goal && (
            <p className="mt-2 border-t pt-2 text-[13px]">
              <span className="font-medium">{t('Done when:')}</span> {goal}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A run of the Lead or an agent as its one message (docs/features/work-item-chat.md#the-result-of-a-run): what it was
 * asked to do, its result — headline, findings with how sure, the text, what is open, links, data, recommendation —,
 * what it asked, what it did, and for the Lead whom it handed the work to.
 */
function RunEntry({ entry, item, activity }: { entry: Extract<LogEntry, { kind: 'run' }>; item: WorkItemDetail; activity: ActivityEvent[] }) {
  const { run, done, finding, briefed, said, handoffs } = entry;
  const lead = run.kind === 'lead' || (!!item.lead && run.agentId === item.lead.id);
  const running = isActive(run);
  const failed = run.status === 'failed' || run.status === 'lost' || run.outcome === 'failed';
  const name = run.agentName ?? (lead ? t('The Lead') : t('Agent'));
  const start = run.startedAt ?? run.createdAt;
  const steps = activity.filter((e) => e.executionId === run.id && !QUIET.has(e.type));
  const report = run.report ?? null;
  // An agent run nobody handed over in the thread (started by a person, or the first run of an item with an agent).
  const assignment = !briefed && run.kind === 'work_item' && run.brief?.trim() && !/^Woken by:/i.test(run.brief) ? run.brief.trim() : '';
  const quiet = !running && !failed && done.length === 0 && !finding && !assignment && !report && said.length === 0 && handoffs.length === 0;

  if (quiet) {
    // A run that left nothing to read: one quiet line.
    return (
      <div id={`run-${run.id}`} className="flex scroll-mt-3 items-center gap-2.5 rounded-lg text-xs text-muted-foreground transition-shadow">
        <AgentAvatar seed={run.agentId} size={20} lead={lead} className="max-sm:hidden" />
        <span className="min-w-0">
          <Name>{name}</Name> {runAction(run, lead, failed)} · <When at={start} />
        </span>
        <SessionLink executionId={run.id} workspaceId={item.workspaceId} />
      </div>
    );
  }

  return (
    <div id={`run-${run.id}`} className="flex scroll-mt-3 gap-2.5 rounded-xl transition-shadow">
      <AgentAvatar seed={run.agentId} size={28} lead={lead} className="mt-0.5 max-sm:hidden" />
      <div className="min-w-0 flex-1">
        <Byline>
          <Name>{name}</Name>
          <Role lead={lead} />
          <span>{runAction(run, lead, failed)}</span>
          <When at={start} />
          {!running && run.finishedAt && <span>· {formatDuration(start, run.finishedAt)}</span>}
          {running && (
            <span className="inline-flex items-center gap-1 font-medium text-indigo-700 dark:text-indigo-300">
              <Loader2 className="size-3 animate-spin" /> {t('working')}
            </span>
          )}
          <SessionLink executionId={run.id} workspaceId={item.workspaceId} />
        </Byline>
        <div
          className={cn(
            'flex flex-col gap-3 rounded-2xl rounded-tl-sm border bg-card px-3.5 py-3',
            failed && 'border-red-200 dark:border-red-900',
            running && 'border-indigo-200 dark:border-indigo-900',
          )}
        >
          {assignment && (
            <div>
              <Label>{t('Assignment')}</Label>
              <Clamp text={assignment} limit={320} height="max-h-20" />
            </div>
          )}
          {running && <AgentSteps executionId={run.id} working agentName={name} />}
          {report ? (
            <ReportView report={report} text={finding} lead={lead} failed={failed} />
          ) : (
            finding && (
              <div>
                <Label>{lead ? t('What follows') : t('Result')}</Label>
                <div className={cn('rounded-lg border px-3 py-2', failed ? 'border-red-200 dark:border-red-900' : 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/20')}>
                  <Clamp text={finding} limit={900} height="max-h-72" />
                </div>
              </div>
            )
          )}
          {failed && run.error && <p className="font-mono text-xs break-words text-red-600">{run.error}</p>}
          {failed && !finding && !report && !run.error && <p className="text-[13px] text-muted-foreground">{t('It ended without a result.')}</p>}
          {said.map((m) => (
            <Said key={m.id} message={m} />
          ))}
          {handoffs.length > 0 && (
            <div className="flex flex-col gap-2">
              {!report && !finding && <Label>{t('What follows')}</Label>}
              {handoffs.map((h) => (
                <Handoff key={h.message.id} message={h.message} target={h.target} />
              ))}
            </div>
          )}
          {done.length > 0 && <WhatItDid entries={done} open={!report && !finding} />}
          {!running && steps.length > 0 && <Steps events={steps} workspaceId={item.workspaceId} />}
        </div>
      </div>
    </div>
  );
}

const CONFIDENCE_TONE: Record<FindingConfidence, string> = {
  confirmed: 'bg-emerald-500/12 text-emerald-800 dark:text-emerald-300',
  likely: 'bg-indigo-500/12 text-indigo-800 dark:text-indigo-300',
  derived: 'bg-amber-500/12 text-amber-800 dark:text-amber-300',
  unchecked: 'bg-muted text-orange-800 dark:text-orange-300',
};

export function confidenceLabel(c: FindingConfidence): string {
  return c === 'confirmed' ? t('confirmed') : c === 'likely' ? t('likely') : c === 'derived' ? t('derived') : t('unchecked');
}

export function openKindLabel(k: OpenPointKind): string {
  return k === 'urgent' ? t('urgent') : k === 'unchecked' ? t('unchecked') : t('unclear');
}

function Tag({ tone, children }: { tone: string; children: ReactNode }) {
  return <span className={cn('mr-1.5 inline-block rounded px-1.5 py-px align-[1px] text-[11px] font-semibold whitespace-nowrap', tone)}>{children}</span>;
}

/** A run's result: headline, findings with how sure, the text, open points, links, data, recommendation. */
function ReportView({ report, text, lead, failed }: { report: RunReport; text: string; lead: boolean; failed: boolean }) {
  const headlineOnly = text.trim() === report.headline.trim();
  return (
    <div className="flex flex-col gap-2.5">
      <div>
        <Label>{lead ? t('What follows') : t('Result')}</Label>
        <p className={cn('text-[15px] leading-snug font-semibold', failed && 'text-red-700 dark:text-red-300')}>{report.headline}</p>
      </div>
      {report.findings.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm">
          {report.findings.map((f, i) => (
            <li key={i} className="break-words">
              <Tag tone={CONFIDENCE_TONE[f.confidence]}>{confidenceLabel(f.confidence)}</Tag>
              {f.text}
              {f.source && (
                <span className="text-muted-foreground">
                  {' '}
                  (
                  {f.source.url ? (
                    <a href={f.source.url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:text-foreground hover:underline">
                      {f.source.label}
                    </a>
                  ) : (
                    f.source.label
                  )}
                  )
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {text && !headlineOnly && <Clamp text={text} limit={report.findings.length ? 500 : 900} height={report.findings.length ? 'max-h-32' : 'max-h-72'} />}
      {report.open.length > 0 && (
        <div>
          <Label>{t('Open')}</Label>
          <ul className="flex flex-col gap-1 text-sm">
            {report.open.map((o, i) => (
              <li key={i} className="break-words">
                <Tag tone={o.kind === 'urgent' ? 'bg-red-500/12 text-red-800 dark:text-red-300' : 'bg-muted text-orange-800 dark:text-orange-300'}>{openKindLabel(o.kind)}</Tag>
                {o.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      {report.links.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {report.links.map((l, i) => (
            <a
              key={i}
              href={l.url}
              target={l.url.startsWith('/') ? undefined : '_blank'}
              rel="noreferrer"
              className="inline-flex max-w-full items-center gap-1 truncate rounded-md border bg-muted/40 px-2 py-0.5 text-xs font-medium hover:border-primary hover:text-primary"
            >
              <Link2 className="size-3 shrink-0" /> {l.label}
            </a>
          ))}
        </div>
      )}
      {report.recommendation && (
        <p className="text-[13px]">
          <span className="font-medium">{t('Recommends:')}</span> {report.recommendation}
        </p>
      )}
      {report.data && Object.keys(report.data).length > 0 && <DataView data={report.data} />}
    </div>
  );
}

/** The run's data: its fields at a glance, the JSON when opened. */
function DataView({ data }: { data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const simple = Object.entries(data).filter(([, v]) => v === null || ['string', 'number', 'boolean'].includes(typeof v));
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{t('Data')}</Label>
        <button type="button" onClick={() => setOpen((o) => !o)} className="cursor-pointer text-xs text-muted-foreground hover:text-foreground hover:underline">
          {open ? t('Hide JSON') : t('Show JSON')}
        </button>
      </div>
      {open ? (
        <pre className="mt-1 max-h-72 overflow-auto font-mono text-xs leading-relaxed">{JSON.stringify(data, null, 2)}</pre>
      ) : (
        simple.length > 0 && (
          <dl className="mt-0.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
            {simple.slice(0, 8).map(([k, v]) => (
              <Fragment key={k}>
                <dt className="font-mono text-muted-foreground">{k}</dt>
                <dd className="break-words">{v === null ? '—' : String(v)}</dd>
              </Fragment>
            ))}
          </dl>
        )
      )}
    </div>
  );
}

/** What else a run wrote: a question (it ended the run), a note. */
function Said({ message: m }: { message: Message }) {
  const question = m.metadata?.question === true;
  return (
    <div className={cn('rounded-lg px-3 py-2', question ? 'border border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/30' : 'bg-muted/40')}>
      <p className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
        {question ? (m.metadata?.forLead === true ? t('Question for the Lead') : t('Question')) : t('Note')} <span className="font-normal normal-case">· {formatTime(m.createdAt)}</span>
      </p>
      <Clamp text={m.content} limit={900} height="max-h-60" />
    </div>
  );
}

/** "Assignment for Case Analyst": the brief the Lead gave, and a jump to that run. */
function Handoff({ message: m, target }: { message: Message; target: Execution | undefined }) {
  const to = typeof m.metadata?.agentName === 'string' ? m.metadata.agentName : t('an agent');
  const self = m.metadata?.agentId === m.senderId;
  const brief = m.content.replace(/^\*\*(Run for [^*]*|I take this on myself\.)\*\*\s*/, '').trim();
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 px-3 py-2 dark:border-indigo-900 dark:bg-indigo-950/30">
      <p className="mb-0.5 flex flex-wrap items-center gap-1 text-[13px] font-medium text-indigo-800 dark:text-indigo-200">
        <ArrowRight className="size-3.5" /> {self ? t('Takes it on itself') : t('Assignment for {agent}', { agent: to })}
        {target && (
          <button type="button" onClick={() => scrollToRun(target.id)} className="ml-1 cursor-pointer text-xs font-normal hover:underline">
            {t('Go to its run')}
          </button>
        )}
      </p>
      <Clamp text={brief || m.content} limit={500} height="max-h-32" />
    </div>
  );
}

/** What the run wrote down it did (its work history entry); folded when the result says it. */
function WhatItDid({ entries, open: startOpen }: { entries: WorkHistoryEntry[]; open: boolean }) {
  const [open, setOpen] = useState(startOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {t('What it did')}
      </button>
      {open && (
        <div className="mt-1 flex flex-col gap-1.5 text-sm text-foreground/90">
          {entries.map((h) => (
            <Markdown key={h.id} text={h.summary} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What is open after the work so far (docs/features/work-item-chat.md#the-result-of-a-run): from each agent's latest
 * result, what it could not check, what is unclear and what is urgent; beside the work history.
 */
export function OpenFromResults({ item, runs }: { item: WorkItemDetail; runs: Execution[] }) {
  const latest = new Map<string, Execution>();
  for (const r of itemRuns(item, runs)) if (r.report) latest.set(r.agentId, r);
  const points: { text: string; kind: OpenPointKind; who: string; run: string }[] = [];
  const seen = new Set<string>();
  for (const r of latest.values()) {
    const who = r.agentName ?? t('Agent');
    const add = (text: string, kind: OpenPointKind) => {
      const key = text.trim().toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      points.push({ text, kind, who, run: r.id });
    };
    for (const o of r.report!.open) add(o.text, o.kind);
    for (const f of r.report!.findings) if (f.confidence === 'unchecked') add(f.text, 'unchecked');
  }
  if (!points.length) return null;
  const order: Record<OpenPointKind, number> = { urgent: 0, unclear: 1, unchecked: 2 };
  points.sort((a, b) => order[a.kind] - order[b.kind]);
  return (
    <section className="rounded-xl border bg-card p-4 shadow-xs" aria-label={t('Open from the results')}>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <CircleAlert className="size-4 text-muted-foreground" /> {t('Open from the results')}
        <span className="font-normal text-muted-foreground tabular-nums">· {points.length}</span>
      </h2>
      <ul className="flex flex-col gap-1.5 text-[13px]">
        {points.map((p, i) => (
          <li key={i} className="break-words">
            <Tag tone={p.kind === 'urgent' ? 'bg-red-500/12 text-red-800 dark:text-red-300' : 'bg-muted text-orange-800 dark:text-orange-300'}>{openKindLabel(p.kind)}</Tag>
            {p.text}{' '}
            <button type="button" onClick={() => scrollToRun(p.run)} className="cursor-pointer text-xs text-muted-foreground hover:text-foreground hover:underline">
              · {p.who}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Label({ children }: { children: ReactNode }) {
  return <p className="mb-0.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">{children}</p>;
}

/** The Lead hands the work to an agent: "Case Lead handed it to Case Analysis", the brief, a jump to that run. */
function HandoffEntry({ message: m, target, workspaceId }: { message: Message; target: Execution | undefined; workspaceId: string }) {
  const to = typeof m.metadata?.agentName === 'string' ? m.metadata.agentName : t('an agent');
  const self = m.metadata?.agentId === m.senderId;
  const brief = m.content.replace(/^\*\*(Run for [^*]*|I take this on myself\.)\*\*\s*/, '').trim();
  return (
    <div className="flex gap-2.5">
      {m.senderId ? (
        <AgentAvatar seed={m.senderId} size={28} lead className="mt-0.5 max-sm:hidden" />
      ) : (
        <div className="mt-0.5 size-7 shrink-0 max-sm:hidden" />
      )}
      <div className="min-w-0 flex-1">
        <Byline>
          <Name>{m.senderName ?? t('The Lead')}</Name>
          <Role lead />
          <span className="inline-flex items-center gap-1">
            {self ? (
              t('takes it on itself')
            ) : (
              <>
                {t('handed it to')} <ArrowRight className="size-3" /> <Name>{to}</Name>
              </>
            )}
          </span>
          <When at={m.createdAt} />
          <SessionLink executionId={m.executionId} workspaceId={workspaceId} />
        </Byline>
        <div className="rounded-2xl rounded-tl-sm border border-indigo-200 bg-indigo-50/50 px-3.5 py-2.5 dark:border-indigo-900 dark:bg-indigo-950/30">
          <Clamp text={brief || m.content} limit={600} height="max-h-40" />
          {target && (
            <button
              type="button"
              onClick={() => scrollToRun(target.id)}
              className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-indigo-700 hover:underline dark:text-indigo-300"
            >
              <ArrowRight className="size-3" /> {t('Go to its run')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Approved: …" — one line; what was approved opens below. */
function DecisionEntry({ message: m }: { message: Message }) {
  const [open, setOpen] = useState(false);
  const approved = m.metadata?.approved !== false;
  const [head, ...rest] = m.content.split(/:\s/);
  const what = rest.join(': ').trim();
  const firstLine = what.split('\n').find((l) => l.trim())?.replace(/\*\*|__|`/g, '').trim() ?? '';
  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={() => what && setOpen((o) => !o)}
        className={cn(
          'inline-flex max-w-[90%] items-center gap-1.5 rounded-full px-3 py-1 text-xs',
          approved ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' : 'bg-red-500/10 text-red-800 dark:text-red-300',
          what && 'cursor-pointer hover:underline',
        )}
        title={formatAbsolute(m.createdAt, true)}
      >
        {approved ? <CheckCircle2 className="size-3.5 shrink-0" /> : <XCircle className="size-3.5 shrink-0" />}
        <span className="font-medium">{decisionLabel(head, approved)}</span>
        {firstLine && <span className="min-w-0 truncate opacity-80">{firstLine}</span>}
        <span className="tabular-nums opacity-70">· {formatTime(m.createdAt)}</span>
      </button>
      {open && (
        <div className="w-full max-w-[90%] rounded-lg border bg-card px-3 py-2 text-sm">
          <Markdown text={what} />
        </div>
      )}
    </div>
  );
}

/** The decision in the reader's language (Deskworks writes it in English: "Approved", "Done – Max checked it" …). */
function decisionLabel(head: string, approved: boolean): string {
  // In God mode the Lead or VICKI decides instead of a person: "Approved by Case Lead (God mode)", "Done – VICKI checked it".
  if (/automatically \(God mode\)/.test(head)) return t('Approved automatically (God mode)');
  const by = /\bby (.+?) \(God mode\)/.exec(head)?.[1] ?? /^Done – (.+?) checked it/.exec(head)?.[1];
  const label = /^Done/.test(head) ? t('Done') : /^Can.t be done/.test(head) ? t('Can’t be done') : approved ? t('Approved') : t('Rejected');
  return by ? t('{decision} by {name} (God mode)', { decision: label, name: by }) : label;
}

/** What a person wrote down they did themselves. */
function NoteEntry({ entry: h, workspaceId }: { entry: WorkHistoryEntry; workspaceId: string }) {
  return (
    <div className="flex gap-2.5">
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 max-sm:hidden dark:text-emerald-300">
        <User className="size-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <Byline>
          <Name>{h.authorName ?? (h.authorType === 'user' ? t('A person') : t('Agent'))}</Name>
          <span>{t('did it themselves')}</span>
          <When at={h.createdAt} />
          {h.executionId && <SessionLink executionId={h.executionId} workspaceId={workspaceId} />}
        </Byline>
        <div className="rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2.5 text-sm">
          <Markdown text={h.summary} />
        </div>
      </div>
    </div>
  );
}

/** A message; an action waiting for approval is one line (its card is at the end). */
function MessageEntry({ message: m, render }: { message: Message; render: (m: Message) => ReactNode }) {
  if (m.senderType === 'system' && m.metadata?.event === 'action_requested') {
    const line = m.content.split('\n')[0].replace(/\*\*/g, '').trim();
    return (
      <p className="flex items-start justify-center gap-1.5 text-[13px] text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-orange-500" />
        <span className="min-w-0 break-words">
          {line} <span className="tabular-nums">· {formatTime(m.createdAt)}</span>
        </span>
      </p>
    );
  }
  return <>{render(m)}</>;
}

/** The steps a run took, until opened only their count. */
function Steps({ events, workspaceId }: { events: ActivityEvent[]; workspaceId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {events.length === 1 ? t('1 step') : t('{count} steps', { count: events.length })}
      </button>
      {open && (
        <ol className="mt-1.5 flex flex-col gap-1.5">
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full border bg-card [&_svg]:size-3">{activityIcon(e.type)}</span>
              <span className="min-w-0 flex-1 pt-0.5 break-words">
                <span className="text-foreground/80">{e.message || e.type}</span>{' '}
                <When at={e.createdAt} />
                {e.executionId && <SessionLink executionId={e.executionId} workspaceId={workspaceId} className="ml-1.5" />}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A long text shows its beginning until opened. */
function Clamp({ text, limit, height, className }: { text: string; limit: number; height: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > limit;
  return (
    <div className={className}>
      <div className={cn('text-sm', long && !open && `relative overflow-hidden ${height}`)}>
        <Markdown text={text} />
        {long && !open && <div aria-hidden className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />}
      </div>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline">
          {open ? t('Show less') : t('Show all')}
        </button>
      )}
    </div>
  );
}
